import { Image } from '@/graphql/graphql';

export interface CameraConfig {
  name: string;
  imageDirectory: string;
  calFile: string;
}

export interface IprjConfig {
  section: string;
  version: 1 | 2;
  units: string;
  rotationOrder: 1 | 2 | 3 | 4 | 6;
  cameraCount: number;
  cameras: CameraConfig[];
}

/**
 * Parse .iprj content into structured config
 */
export function parseIprj(content: string): IprjConfig {
  const lines = content.split(/\r?\n/);
  let section = '';
  const raw: Record<string, string> = {};

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith(';') || trimmed.startsWith('#')) {
      continue;
    }
    const sectionMatch = trimmed.match(/^\[(.+)\]$/);
    if (sectionMatch) {
      section = sectionMatch[1];
      continue;
    }
    const kv = trimmed.split('=');
    if (kv.length >= 2) {
      const key = kv.shift()!.trim();
      const value = kv.join('=').trim();
      raw[key] = value;
    }
  }

  const version = parseInt(raw['Version'], 10);
  const units = raw['Units'];
  const rotationOrder = parseInt(raw['RotationOrder'], 10);
  const cameraCount = parseInt(raw['CameraCount'], 10);

  const cameras: CameraConfig[] = [];
  for (let i = 0; i < cameraCount; i++) {
    const name = raw[`Name${i}`] || '';
    const imageDirectory = raw[`ImageDirectory${i}`].replaceAll('\\', '/') || '';
    const calFile = raw[`CalFile${i}`].replaceAll('\\', '/') || '';
    cameras.push({ name, imageDirectory, calFile });
  }

  return { section, version, units, rotationOrder, cameraCount, cameras };
}

export interface ImageListEntry {
  image: string;
  xyz: [number, number, number];
  hrp?: [number, number, number];
  mat?: [[number, number, number], [number, number, number], [number, number, number]];
  camera: number;
}

export interface ImageListConfig {
  section: string;
  entries: ImageListEntry[];
}

/**
 * Parse .iprj list content into structured config
 */
export function parseIprjList(content: string): ImageListConfig {
  const lines = content.split(/\r?\n/);
  let section = '';
  const entries: ImageListEntry[] = [];
  let current: Partial<ImageListEntry> = {};

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line || line.startsWith(';') || line.startsWith('#')) continue;

    const sectMatch = line.match(/^\[(.+)\]$/);
    if (sectMatch) {
      section = sectMatch[1];
      continue;
    }

    const [key, ...rest] = line.split('=');
    const value = rest.join('=').trim();

    switch (key) {
      case 'Image':
        // If there is an existing entry, push it before starting new
        if (current.image !== undefined) {
          entries.push(current as ImageListEntry);
          current = {};
        }
        current.image = value;
        break;
      case 'Xyz': {
        const parts = value.split(/\s+/).map(parseFloat);
        if (parts.length === 3) current.xyz = [parts[0], parts[1], parts[2]];
        break;
      }
      case 'Hrp': {
        const parts = value.split(/\s+/).map(parseFloat);
        if (parts.length === 3) current.hrp = [parts[0], parts[1], parts[2]];
        break;
      }
      case 'Mat': {
        const nums = value.split(/\s+/).map(parseFloat);
        if (nums.length === 9) {
          current.mat = [
            [nums[0], nums[1], nums[2]],
            [nums[3], nums[4], nums[5]],
            [nums[6], nums[7], nums[8]],
          ];
        }
        break;
      }
      case 'Camera':
        current.camera = parseInt(value, 10);
        break;
      default:
        // ignore unknown keys
        break;
    }
  }

  // push last entry if present
  if (current.image !== undefined) {
    entries.push(current as ImageListEntry);
  }

  return { section, entries };
}

export interface PositionGroup {
  xyz: [number, number, number];
  entries: ImageListEntry[];
}

export interface PositionGroup2 {
  xyz: [number, number, number];
  entries: Omit<Image, 'layer'>[];
}

/**
 * Groups ImageListEntry objects by identical xyz coordinates OR identical name OR identical image.
 * @param entries Array of ImageListEntry
 * @returns Array of PositionGroup
 */
export function groupImagesByPosition(entries: ImageListEntry[]): PositionGroup[] {
  const groups: PositionGroup[] = [];
  const xyzMap = new Map<string, number>();
  const shotMap = new Map<string, number>();

  // debug for hu
  entries = entries.some((e) => e.image.includes('160616_09142'))
    ? entries.filter((entry) => entry.image.includes('160616_09142'))
    : entries;

  const limit = Math.min(entries.length, 1_000);

  const shotKeyOf = (imagePath: string): string => {
    // filename without folders
    const file = imagePath.replace(/^.*[\\/]/, '');
    // remove extension
    const stem = file.replace(/\.[^.]+$/, '');
    // strip trailing `_Camera_<digits>` if present
    const withoutCamera = stem.replace(/_Camera_\d+$/i, '');

    return withoutCamera;
  };

  for (let i = 0; i < limit; i++) {
    const entry = entries[i];
    const xyzKey = entry.xyz.join(',');
    const shotKey = shotKeyOf(entry.image);

    const gi = xyzMap.get(xyzKey);
    const gj = shotMap.get(shotKey);

    const chosenIdx: number | undefined = gi ?? gj;

    if (chosenIdx === undefined) {
      const newIdx = groups.length;
      groups.push({ xyz: entry.xyz, entries: [entry] });
      xyzMap.set(xyzKey, newIdx);
      shotMap.set(shotKey, newIdx);
    } else {
      groups[chosenIdx].entries.push(entry);
      // backfill maps so future matches snap to this group
      if (!xyzMap.has(xyzKey)) xyzMap.set(xyzKey, chosenIdx);
      if (!shotMap.has(shotKey)) shotMap.set(shotKey, chosenIdx);
    }
  }

  return groups;
}

/**
 * Groups Image objects by layerId+groupId combination if available, otherwise by identical xyz coordinates OR identical name OR identical image.
 * @param entries Array of Image
 * @returns Array of PositionGroup
 */
export function groupImagesByPosition2(entries: Omit<Image, 'layer'>[]): PositionGroup2[] {
  const groups: PositionGroup2[] = [];
  const layerGroupIdMap = new Map<string, number>();
  const xyzMap = new Map<string, number>();
  const shotMap = new Map<string, number>();

  const shotKeyOf = (imagePath: string): string => {
    // filename without folders
    const file = imagePath.replace(/^.*[\\/]/, '');
    // remove extension
    const stem = file.replace(/\.[^.]+$/, '');
    // strip trailing `_Camera_<digits>` if present
    const withoutCamera = stem.replace(/_Camera_\d+$/i, '');

    return withoutCamera;
  };

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    const xyzKey = entry.location.coords.join(',');
    const shotKey = shotKeyOf(entry.filename);

    // Priority 1: Group by layerId+groupId combination if both are available
    const layerGroupKey = entry.layerId && entry.groupId ? `${entry.layerId}-${entry.groupId}` : undefined;
    const gLayerGroupId = layerGroupKey ? layerGroupIdMap.get(layerGroupKey) : undefined;
    // Priority 2: Group by xyz coordinates
    const gXyz = xyzMap.get(xyzKey);
    // Priority 3: Group by shot name
    const gShot = shotMap.get(shotKey);

    const chosenIdx: number | undefined = gLayerGroupId ?? gXyz ?? gShot;

    if (chosenIdx === undefined) {
      const newIdx = groups.length;
      groups.push({ xyz: entry.location.coords, entries: [entry] });

      // Update all relevant maps
      if (layerGroupKey) layerGroupIdMap.set(layerGroupKey, newIdx);
      xyzMap.set(xyzKey, newIdx);
      shotMap.set(shotKey, newIdx);
    } else {
      groups[chosenIdx].entries.push(entry);

      // Backfill maps so future matches snap to this group
      if (layerGroupKey && !layerGroupIdMap.has(layerGroupKey)) {
        layerGroupIdMap.set(layerGroupKey, chosenIdx);
      }
      if (!xyzMap.has(xyzKey)) xyzMap.set(xyzKey, chosenIdx);
      if (!shotMap.has(shotKey)) shotMap.set(shotKey, chosenIdx);
    }
  }

  return groups;
}

export interface CameraCalibrationConfig {
  section: string;
  version: number;
  type: number;
  dx: number;
  dy: number;
  Nx: number;
  Ny: number;
  fx: number;
  fy: number;
  Cx: number;
  Cy: number;
  k1: number;
  k2: number;
  k3: number;
  k4: number;
  P1: number;
  P2: number;
}

/**
 * Parse camera calibration content into structured config
 */
export function parseCameraConfig(content: string): CameraCalibrationConfig {
  const lines = content.split(/\r?\n/);
  let section = '';
  const raw: Record<string, string> = {};

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith(';') || trimmed.startsWith('#')) continue;
    const sect = trimmed.match(/^\[(.+)\]$/);
    if (sect) {
      section = sect[1];
      continue;
    }
    const [key, ...rest] = trimmed.split('=');
    raw[key] = rest.join('=').trim();
  }

  return {
    section,
    version: parseInt(raw['Version'] || '0', 10),
    type: parseInt(raw['Type'] || '0', 10),
    dx: parseFloat(raw['dx'] || '0'),
    dy: parseFloat(raw['dy'] || '0'),
    Nx: parseInt(raw['Nx'] || '0', 10),
    Ny: parseInt(raw['Ny'] || '0', 10),
    fx: parseFloat(raw['fx'] || '0'),
    fy: parseFloat(raw['fy'] || '0'),
    Cx: parseFloat(raw['Cx'] || '0'),
    Cy: parseFloat(raw['Cy'] || '0'),
    k1: parseFloat(raw['k1'] || '0'),
    k2: parseFloat(raw['k2'] || '0'),
    k3: parseFloat(raw['k3'] || '0'),
    k4: parseFloat(raw['k4'] || '0'),
    P1: parseFloat(raw['P1'] || '0'),
    P2: parseFloat(raw['P2'] || '0'),
  };
}
